import React from 'react'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import i18n from 'config/i18n'

import { SubmissionState, SUBMISSION_VIEW_ENUM, PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { TaskState, LINK_IN_BIO_TYPE_ENUM } from 'state/tasks'
import { parseBackendDate } from 'services/dates'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { UrlCell, UrlCellProps } from 'components/Shared/MuiTable/Cell/UrlCell'
import { MediaCell, MediaCellProps } from 'components/Shared/MuiTable/Cell/MediaCell'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { CommentCell } from 'components/Shared/MuiTable/Cell/CommentCell'
import { FollowerTierOption, COUNTRY_ENUM } from 'services/api/constants'
import { getFollowerTierDisplay } from 'services/numbers'
import { PLATFORM_ENUM } from 'services/api/constants'
import { TagContentType } from 'particles/Tag'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { GridColDefProps } from '../SimpleTable/constants'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'

export interface RowsProps {
    // Note: Row id is treated from MUI DataGridPro component as list key, don't use index for dynamic list key
    // issue related: https://gitlab.com/partipost-dev/engineering/backlog-items/-/issues/246
    id: string
    uuid: string
    socialMediaInfo: { profiles: SocialMediaAccount[] }
    screenshots: string[]
    postingTime: {
        value: string
        color?: string
    }
    viewCount: {
        value: string
        color?: string
    }
    linkInBioUrl: string
    lastReviewedBy: {
        value?: string
    }
    lastReviewedAt: {
        value?: string
    }
    editsRequested: {
        submission?: SubmissionState
        contentType: TagContentType
    }
    partiposterTags: {
        profileData: PartiposterProfile
        submissionUuid: string
        campaignUuid: string
    }
}

export const getColumns = (
    viewType: SUBMISSION_VIEW_ENUM | null,
    showLinkInBio: boolean,
    canManageTag: boolean,
): GridColDefProps[] => {
    if (viewType === 'edits_requested') {
        return [
            {
                field: 'profile',
                type: 'string',
                headerName: i18n.t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: i18n.t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: i18n.t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: i18n.t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: i18n.t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    {
                        field: FIELDS_TYPE.IG_USERNAME,
                        headerName: i18n.t('submission.table.instagramUsernameLabel'),
                    },
                ],
            },
            {
                field: 'igFollowers',
                headerName: i18n.t('submission.table.followerRangeLabel'),
                width: 155,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
            },
            {
                field: 'screenshots',
                headerName: i18n.t('submission.table.screenshotLabel'),
                width: 126,
                renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'viewCount',
                headerName: i18n.t('submission.table.viewCountLabel'),
                width: 125,
                sortable: false,
                filterable: false,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            },
            ...(showLinkInBio
                ? [
                      {
                          field: 'linkInBioUrl',
                          type: 'string',
                          headerName: i18n.t('submission.table.linkInBioLabel'),
                          width: 180,
                          renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            {
                field: 'lastReviewedBy',
                type: 'string',
                headerName: i18n.t('submission.table.lastReviewedLabel'),
                minWidth: 180,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'lastReviewedAt',
                type: 'string',
                headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                minWidth: 180,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'editsRequested',
                type: 'string',
                headerName: i18n.t('submission.table.reasonEditRequestLabel'),
                width: 200,
                renderCell: (props: GridRenderCellParams) => <CommentCell {...props} />,
                sortable: false,
                filterable: false,
            },
            ...(canManageTag
                ? [
                      {
                          field: 'partiposterTags',
                          headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                          width: 240,
                          minWidth: 200,
                          renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
        ]
    } else {
        return [
            {
                field: 'profile',
                type: 'string',
                headerName: i18n.t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: i18n.t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: i18n.t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: i18n.t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: i18n.t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    {
                        field: FIELDS_TYPE.IG_USERNAME,
                        headerName: i18n.t('submission.table.instagramUsernameLabel'),
                    },
                ],
            },
            {
                field: 'igFollowers',
                headerName: i18n.t('submission.table.followerRangeLabel'),
                width: 155,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
            },
            {
                field: 'screenshots',
                headerName: i18n.t('submission.table.screenshotLabel'),
                width: 126,
                renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'viewCount',
                headerName: i18n.t('submission.table.viewCountLabel'),
                width: 125,
                sortable: false,
                filterable: false,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            },
            {
                field: 'postingTime',
                type: 'dateTime',
                headerName: i18n.t('submission.table.submittedTimeLabel'),
                width: 150,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            ...(showLinkInBio
                ? [
                      {
                          field: 'linkInBioUrl',
                          type: 'string',
                          headerName: i18n.t('submission.table.linkInBioLabel'),
                          width: 200,
                          renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(viewType !== SUBMISSION_VIEW_ENUM.PENDING
                ? [
                      {
                          field: 'lastReviewedBy',
                          type: 'string',
                          headerName: i18n.t('submission.table.lastReviewedLabel'),
                          minWidth: 180,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                      {
                          field: 'lastReviewedAt',
                          type: 'string',
                          headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                          minWidth: 180,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(canManageTag
                ? [
                      {
                          field: 'partiposterTags',
                          headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                          width: 240,
                          minWidth: 160,
                          renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
        ]
    }
}

export const getRows = (
    viewType: SUBMISSION_VIEW_ENUM | null,
    campaignUuid: string,
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
    task: TaskState,
    followerTierOptions: FollowerTierOption[],
    country?: COUNTRY_ENUM,
): RowsProps[] => {
    return submissions.map(submission => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.filter(account => account.platform === PLATFORM_ENUM.IG)[0]
        let linkInBioUrl
        if (task && task.data) {
            if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK) {
                linkInBioUrl = task.data['link_in_bio_url']
            } else if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK) {
                if (submission.submission_task != undefined) {
                    linkInBioUrl = submission.submission_task.data['link_in_bio_url']
                }
            }
        }
        return {
            id: submission.uuid,
            uuid: submission.uuid,
            profile: { profile },
            socialMediaInfo: { profiles: [igProfile] },
            igFollowers: {
                value:
                    igProfile && country && followerTierOptions
                        ? getFollowerTierDisplay(igProfile, country, followerTierOptions)
                        : '',
            },
            screenshots:
                viewType === 'pending' || viewType === 'edits_requested'
                    ? submission && submission.data && submission.data.files
                    : submission && submission.reviewed_data && submission.reviewed_data.files,
            postingTime:
                viewType === 'pending' || viewType === 'edits_requested'
                    ? { value: '' }
                    : submission.reviewed_data && country
                      ? { value: parseBackendDate(submission.reviewed_data.posting_time, country) }
                      : { value: '' },
            viewCount:
                viewType === 'pending' || viewType === 'edits_requested'
                    ? { value: submission && submission.data && submission.data.view_count }
                    : { value: submission && submission.reviewed_data && submission.reviewed_data.view_count },
            linkInBioUrl: linkInBioUrl,
            lastReviewedBy: {
                value: submission.reviewed_by_system
                    ? i18n.t('statusLabel.autoApproved')
                    : submission.moved_out_of_pending_by?.name,
            },
            lastReviewedAt: {
                value: submission.moved_out_of_pending_at
                    ? parseBackendDate(submission.moved_out_of_pending_at, country, { showTime: true })
                    : undefined,
            },
            editsRequested:
                viewType === 'edits_requested'
                    ? { submission: submission, contentType: 'warning' }
                    : { submission: undefined, contentType: 'warning' },
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission.submission_uuid,
                campaignUuid: campaignUuid,
            },
        }
    })
}
